import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { formatDate } from '../../utils/dateUtils';
import PeopleMultiSelect from '../../components/PeopleMultiSelect';

const ConductAudit = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditId } = useParams();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const schedule = location.state?.schedule;
  const [recordId, setRecordId] = useState(location.state?.auditRecordId || null);
  const templateIdFromQuery = searchParams.get('template_id');

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState([]);
  const [summaryNotes, setSummaryNotes] = useState('');
  const [auditTitle, setAuditTitle] = useState('');
  const [auditLocation, setAuditLocation] = useState('');
  const [auditDepartment, setAuditDepartment] = useState('');
  const [leadAuditorId, setLeadAuditorId] = useState('');
  const [leadAuditorName, setLeadAuditorName] = useState('');
  // Audit team: co-auditors (joint auditors) and auditees (participating members)
  const [coAuditors, setCoAuditors] = useState([]);
  const [auditees, setAuditees] = useState([]);
  const [eligibleAuditors, setEligibleAuditors] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [auditDate, setAuditDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCAPAModal, setShowCAPAModal] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [capaForm, setCAPAForm] = useState({
    title: '',
    description: '',
    type: 'Corrective',
    severity: 'Medium',
    root_cause: '',
    corrective_action: '',
    preventive_action: '',
    assigned_to: '',
    assigned_to_name: '',
    due_date: ''
  });
  const [users, setUsers] = useState([]);

  const severityOptions = ['Minor', 'Moderate', 'Major', 'Critical'];
  const categoryColors = {
    'Safety': 'bg-red-50 border-red-200',
    'Environment': 'bg-green-50 border-green-200',
    'Fire': 'bg-orange-50 border-orange-200',
    'Equipment': 'bg-blue-50 border-blue-200',
    'Behaviour': 'bg-purple-50 border-purple-200',
    'Housekeeping': 'bg-yellow-50 border-yellow-200'
  };

  useEffect(() => {
    // Must have at least one entry point: schedule, template_id, or existing record
    if (!schedule && !templateIdFromQuery && !recordId) {
      navigate('/audits');
      return;
    }

    const loadExistingRecord = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Load the existing record and its template
        const recordRes = await axios.get(`${API}/audit-records/${recordId}`, { headers });
        const existingRecord = recordRes.data;

        // /users needs users:read, which an auditor conducting an audit may not
        // have — tolerate its failure rather than blanking the whole page, and
        // source the audit-team pickers from the purpose-built endpoints.
        const [templateRes, usersRes, auditorsRes, directoryRes] = await Promise.all([
          axios.get(`${API}/audit-templates/${existingRecord.template_id}`, { headers }),
          axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/audit-schedules/eligible-auditors`, { headers }).catch(() => ({ data: { auditors: [] } })),
          axios.get(`${API}/audit-participant-directory`, { headers }).catch(() => ({ data: { users: [] } }))
        ]);

        setTemplate(templateRes.data);
        setUsers(usersRes.data);
        setEligibleAuditors(auditorsRes.data?.auditors || []);
        setDirectory(directoryRes.data?.users || []);
        setAuditTitle(existingRecord.audit_title || '');
        setAuditLocation(existingRecord.location || '');
        setAuditDepartment(existingRecord.department || '');
        setLeadAuditorId(existingRecord.lead_auditor_id || '');
        setLeadAuditorName(existingRecord.lead_auditor_name || '');
        setCoAuditors(existingRecord.co_auditors || []);
        setAuditees(existingRecord.auditees || []);
        setAuditDate(existingRecord.audit_date || '');
        setSummaryNotes(existingRecord.summary_notes || '');

        // Restore saved findings, ensuring all template items are present
        const savedFindings = existingRecord.findings || [];
        const savedMap = new Map(savedFindings.map(f => [f.checklist_item_id || f.id, f]));
        const mergedFindings = templateRes.data.checklist_items.map(item => {
          const saved = savedMap.get(item.id);
          if (saved) return saved;
          return {
            id: item.id,
            checklist_item_id: item.id,
            question: item.question,
            category: item.category,
            is_critical: item.is_critical,
            status: '',
            severity: '',
            observations: '',
            evidence_urls: [],
            recommended_action: ''
          };
        });
        setFindings(mergedFindings);
      } catch (err) {
        console.error('Failed to load existing record:', err);
        showError('Failed to load audit record');
      } finally {
        setLoading(false);
      }
    };

    const loadTemplate = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Determine which template to load
        const templateId = templateIdFromQuery || schedule?.template_id;

        // See note above: /users may 403 for an auditor, so it must not be able
        // to blank the form.
        const [templateRes, usersRes, auditorsRes, directoryRes] = await Promise.all([
          axios.get(`${API}/audit-templates/${templateId}`, { headers }),
          axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/audit-schedules/eligible-auditors`, { headers }).catch(() => ({ data: { auditors: [] } })),
          axios.get(`${API}/audit-participant-directory`, { headers }).catch(() => ({ data: { users: [] } }))
        ]);

        setTemplate(templateRes.data);
        setUsers(usersRes.data);
        setEligibleAuditors(auditorsRes.data?.auditors || []);
        setDirectory(directoryRes.data?.users || []);

        // Set audit details from schedule or generate defaults
        const title = schedule?.audit_title || `${templateRes.data.audit_type} - ${formatDate(new Date())}`;
        setAuditTitle(title);
        setAuditLocation(schedule?.location || '');
        setAuditDepartment(schedule?.department || '');
        setLeadAuditorId(schedule?.auditor_id || user.id);
        setLeadAuditorName(schedule?.auditor_name || user.name);
        // Carry the team planned on the schedule into the conducted audit
        setCoAuditors(schedule?.co_auditors || []);
        setAuditDate(schedule?.scheduled_date || new Date().toISOString());

        // Initialize findings from checklist
        const initialFindings = templateRes.data.checklist_items.map(item => ({
          id: item.id,
          checklist_item_id: item.id,
          question: item.question,
          category: item.category,
          is_critical: item.is_critical,
          status: '',
          severity: '',
          observations: '',
          evidence_urls: [],
          recommended_action: ''
        }));
        setFindings(initialFindings);
      } catch (err) {
        console.error('Failed to fetch template:', err);
        showError('Failed to load audit template');
      } finally {
        setLoading(false);
      }
    };

    // If we have a recordId but no schedule, we're editing an existing draft
    if (recordId && !schedule) {
      loadExistingRecord();
    } else {
      loadTemplate();
    }
  }, [schedule, navigate, recordId]);

  const updateFinding = (itemId, field, value) => {
    setFindings(findings.map(f =>
      f.id === itemId ? { ...f, [field]: value } : f
    ));
  };

  const openCAPAModal = (finding) => {
    setSelectedFinding(finding);
    const auditTitle = schedule?.audit_title || `${template?.audit_type || 'Audit'} - ${formatDate(new Date())}`;
    setCAPAForm({
      title: `${auditTitle} - ${finding.question}`,
      description: finding.observations || `Non-conformance identified during audit: ${finding.question}`,
      type: 'Corrective',
      severity: finding.severity || 'Medium',
      root_cause: finding.observations || '',
      corrective_action: finding.recommended_action || '',
      preventive_action: '',
      assigned_to: user.user_id,
      assigned_to_name: user.name,
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    setShowCAPAModal(true);
  };

  const handleAssignedToChange = (e) => {
    const userId = e.target.value;
    const selectedUser = users.find(u => u.id === userId);
    setCAPAForm({
      ...capaForm,
      assigned_to: userId,
      assigned_to_name: selectedUser?.name || ''
    });
  };

  const createCAPA = async () => {
    if (!capaForm.title || !capaForm.assigned_to) {
      showWarning('Please fill in title and assign to someone');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Map frontend form fields to backend CAPACreate model
      const auditTitle = schedule?.audit_title || `${template?.audit_type || 'Audit'} - ${formatDate(new Date())}`;
      const capaData = {
        source: 'Audit',
        source_id: schedule?.id || null,
        source_title: auditTitle,
        title: capaForm.title,
        description: capaForm.description,
        capa_type: capaForm.type,
        severity: capaForm.severity,
        priority: 'Medium',
        assigned_to: capaForm.assigned_to,
        assigned_to_name: capaForm.assigned_to_name,
        due_date: capaForm.due_date,
        root_cause: capaForm.root_cause,
        evidence_required: true,
        effectiveness_review_required: false,
        ssem_dimension: null,
        site: auditLocation || null,
        department: schedule?.department || null
      };

      const res = await axios.post(`${API}/capa`, capaData, { headers });

      // Update finding with CAPA ID
      updateFinding(selectedFinding.id, 'capa_id', res.data.id);

      showSuccess('CAPA created successfully and linked to audit finding!');
      setShowCAPAModal(false);
    } catch (err) {
      console.error('Failed to create CAPA:', err);
      showError(err.response?.data?.detail || 'Failed to create CAPA');
    }
  };

  const saveDraft = async () => {
    await saveAudit('Draft');
  };

  const submitAudit = async () => {
    // Validate all items have status
    const incomplete = findings.filter(f => !f.status);
    if (incomplete.length > 0) {
      showWarning(`Please mark status for all checklist items. ${incomplete.length} item(s) remaining.`);
      return;
    }

    // Validate failed items have severity
    const failedWithoutSeverity = findings.filter(f => f.status === 'Fail' && !f.severity);
    if (failedWithoutSeverity.length > 0) {
      showWarning('Please select severity for all failed items.');
      return;
    }

    await saveAudit('Submitted');
  };

  const saveAudit = async (status) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Calculate compliance stats using ALL checklist items as denominator
      const totalChecks = findings.length;
      const evaluatedFindings = findings.filter(f => f.status);
      const passedChecks = evaluatedFindings.filter(f => f.status === 'Pass').length;
      const failedChecks = evaluatedFindings.filter(f => f.status === 'Fail').length;
      const naChecks = evaluatedFindings.filter(f => f.status === 'N/A').length;
      const applicable = totalChecks - naChecks;
      const complianceScore = applicable > 0 ? (passedChecks / applicable) * 100 : 0;

      // Count findings by severity
      const criticalFindings = evaluatedFindings.filter(f => f.status === 'Fail' && f.severity === 'Critical').length;
      const majorFindings = evaluatedFindings.filter(f => f.status === 'Fail' && f.severity === 'Major').length;
      const moderateFindings = evaluatedFindings.filter(f => f.status === 'Fail' && f.severity === 'Moderate').length;
      const minorFindings = evaluatedFindings.filter(f => f.status === 'Fail' && f.severity === 'Minor').length;

      // Handle both scheduled audits and quick-start audits
      const auditRecord = {
        schedule_id: schedule?.id || null,
        template_id: templateIdFromQuery || schedule?.template_id,
        template_name: template?.template_name || schedule?.template_name,
        audit_title: auditTitle,
        audit_type: template?.audit_type || 'General',
        lead_auditor_id: leadAuditorId || user.id,
        lead_auditor_name: leadAuditorName || user.name,
        // Audit team. Guard against the lead also appearing as a co-auditor,
        // which the API rejects with a 400.
        co_auditors: (coAuditors || []).filter(c => c.user_id !== (leadAuditorId || user.id)),
        auditees: auditees || [],
        audit_date: auditDate || new Date().toISOString(),
        location: auditLocation || '',
        area: auditLocation || 'General',
        department: auditDepartment || '',
        workflow_status: status,
        findings: findings,
        summary_notes: summaryNotes,
        total_checks: totalChecks,
        passed_checks: passedChecks,
        failed_checks: failedChecks,
        na_checks: naChecks,
        compliance_score: complianceScore,
        critical_findings: criticalFindings,
        major_findings: majorFindings,
        moderate_findings: moderateFindings,
        minor_findings: minorFindings
      };

      if (recordId) {
        // Update the existing audit record
        await axios.put(`${API}/audit-records/${recordId}`, auditRecord, { headers });
      } else {
        // Create a new audit record and track its ID for subsequent saves
        const response = await axios.post(`${API}/audit-records`, auditRecord, { headers });
        setRecordId(response.data.id);
      }

      // Update schedule status to Completed when audit is submitted
      if (schedule?.id && status === 'Submitted') {
        try {
          await axios.put(
            `${API}/audit-schedules/${schedule.id}`,
            { status: 'Completed' },
            { headers }
          );
        } catch (scheduleErr) {
          // Non-blocking - audit was saved successfully
        }
      }

      showSuccess(`Audit ${status === 'Draft' ? 'saved as draft' : 'submitted'} successfully!`);
      navigate('/audits/records');
    } catch (err) {
      console.error('Failed to save audit:', err);
      showError('Failed to save audit: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getProgress = () => {
    const completed = findings.filter(f => f.status).length;
    return {
      completed,
      total: findings.length,
      percentage: findings.length > 0 ? (completed / findings.length) * 100 : 0
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  const progress = getProgress();

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits/records')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audit Records
          </button>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">🔍 Conduct Audit</h1>
            <p className="text-xl text-gray-700">{auditTitle}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <p className="text-sm text-gray-600">Template</p>
                <p className="font-semibold">{template?.template_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-semibold">{template?.audit_type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-semibold">{auditLocation || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-semibold">{formatDate(new Date())}</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{progress.completed} / {progress.total} items completed</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-indigo-600 h-3 rounded-full transition-all"
                  style={{ width: `${progress.percentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Team — lead auditor, co-auditors (joint auditors), auditees */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">👥 Audit Team</h2>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Lead Auditor <span className="text-red-500">*</span>
              </label>
              <select
                value={leadAuditorId}
                onChange={(e) => {
                  const id = e.target.value;
                  const picked = eligibleAuditors.find(a => a.id === id);
                  setLeadAuditorId(id);
                  setLeadAuditorName(picked?.name || '');
                  // The lead can't also be a co-auditor
                  setCoAuditors(prev => prev.filter(c => c.user_id !== id));
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                {/* Keep the prefilled lead selectable even if they aren't in the
                    eligible list (e.g. the current user quick-starting an audit) */}
                {leadAuditorId && !eligibleAuditors.some(a => a.id === leadAuditorId) && (
                  <option value={leadAuditorId}>{leadAuditorName || 'Current user'}</option>
                )}
                <option value="">
                  {eligibleAuditors.length === 0 ? 'No eligible auditors found' : 'Select the lead auditor'}
                </option>
                {eligibleAuditors.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.role_label ? ` - ${a.role_label}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <PeopleMultiSelect
              label="Co-Auditors / Joint Auditors"
              helpText="Optional. Add joint auditors from your organisation, or external auditors (e.g. certification body) who have no login."
              value={coAuditors}
              onChange={setCoAuditors}
              options={eligibleAuditors}
              excludeIds={leadAuditorId ? [leadAuditorId] : []}
              allowExternal
              placeholder="Search eligible auditors — pick multiple"
            />

            <PeopleMultiSelect
              label="Auditees (Participating Members)"
              helpText="The people being audited. Add employees, or external participants such as contractor representatives."
              value={auditees}
              onChange={setAuditees}
              options={directory}
              allowExternal
              placeholder="Search employees — pick multiple"
            />
          </div>
        </div>

        {/* Checklist Items */}
        <div className="space-y-4 mb-6">
          {findings.map((finding, index) => {
            const item = template.checklist_items.find(ci => ci.id === finding.id);
            return (
              <div
                key={finding.id}
                className={`bg-white rounded-xl shadow-md p-6 border-2 ${categoryColors[finding.category]}`}
              >
                <div className="flex items-start gap-4">
                  <span className="text-2xl font-bold text-gray-400">{index + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-800">{finding.question}</h3>
                        <div className="flex gap-2 mt-2">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {finding.category}
                          </span>
                          {finding.is_critical && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-bold">
                              ⚠️ Critical
                            </span>
                          )}
                        </div>
                        {item?.guidance_notes && (
                          <p className="text-sm text-gray-600 mt-2 italic">
                            💡 {item.guidance_notes}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status Selection */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <button
                        onClick={() => updateFinding(finding.id, 'status', 'Pass')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          finding.status === 'Pass'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ✓ Pass
                      </button>
                      <button
                        onClick={() => updateFinding(finding.id, 'status', 'Fail')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          finding.status === 'Fail'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ✗ Fail
                      </button>
                      <button
                        onClick={() => updateFinding(finding.id, 'status', 'N/A')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          finding.status === 'N/A'
                            ? 'bg-gray-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        N/A
                      </button>
                    </div>

                    {/* Severity for Failed Items */}
                    {finding.status === 'Fail' && (
                      <div className="mb-3">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Severity <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                          {severityOptions.map(severity => (
                            <button
                              key={severity}
                              onClick={() => updateFinding(finding.id, 'severity', severity)}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold ${
                                finding.severity === severity
                                  ? severity === 'Critical'
                                    ? 'bg-red-600 text-white'
                                    : severity === 'Major'
                                    ? 'bg-orange-600 text-white'
                                    : severity === 'Moderate'
                                    ? 'bg-yellow-600 text-white'
                                    : 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {severity}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Observations */}
                    {finding.status && finding.status !== 'Pass' && (
                      <div className="mb-3">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Observations / Details
                        </label>
                        <textarea
                          value={finding.observations}
                          onChange={(e) => updateFinding(finding.id, 'observations', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          rows={2}
                          placeholder="Describe what you observed..."
                        />
                      </div>
                    )}

                    {/* Recommended Action for Failed Items */}
                    {finding.status === 'Fail' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Recommended Action
                        </label>
                        <textarea
                          value={finding.recommended_action}
                          onChange={(e) => updateFinding(finding.id, 'recommended_action', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          rows={2}
                          placeholder="Recommended corrective action..."
                        />
                        
                        {/* Create CAPA Button */}
                        <div className="mt-3">
                          {finding.capa_id ? (
                            <div className="flex items-center gap-2">
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm font-semibold">
                                ✓ CAPA Created
                              </span>
                              <button
                                onClick={() => navigate(`/capa/${finding.capa_id}`)}
                                className="text-blue-600 hover:underline text-sm"
                              >
                                View CAPA
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => openCAPAModal(finding)}
                              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Create CAPA
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Notes */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <label className="block text-lg font-bold text-gray-800 mb-3">
            Summary Notes
          </label>
          <textarea
            value={summaryNotes}
            onChange={(e) => setSummaryNotes(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            rows={4}
            placeholder="Overall observations, recommendations, or additional comments..."
          />
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white rounded-xl shadow-lg p-6 flex gap-4">
          <button
            onClick={() => navigate('/audits/records')}
            className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={saveDraft}
            disabled={saving}
            className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold disabled:bg-gray-300"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={submitAudit}
            disabled={saving || progress.completed < progress.total}
            className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>

        {/* CAPA Creation Modal */}
        {showCAPAModal && selectedFinding && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Create CAPA from Finding</h2>
                <button onClick={() => setShowCAPAModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto" style={{maxHeight: 'calc(90vh - 140px)'}}>
                {/* Finding Reference */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold text-blue-800">Finding:</p>
                  <p className="text-gray-800">{selectedFinding.question}</p>
                  <p className="text-sm text-gray-600 mt-1">Category: {selectedFinding.category}</p>
                </div>

                <div className="space-y-4">
                  {/* CAPA Title */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      CAPA Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={capaForm.title}
                      onChange={(e) => setCAPAForm({...capaForm, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Brief title for this CAPA"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                      <select
                        value={capaForm.type}
                        onChange={(e) => setCAPAForm({...capaForm, type: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="Corrective">Corrective</option>
                        <option value="Preventive">Preventive</option>
                      </select>
                    </div>

                    {/* Severity */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Severity</label>
                      <select
                        value={capaForm.severity}
                        onChange={(e) => setCAPAForm({...capaForm, severity: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={capaForm.description}
                      onChange={(e) => setCAPAForm({...capaForm, description: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows={3}
                      placeholder="Detailed description of the issue"
                    />
                  </div>

                  {/* Root Cause */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Root Cause Analysis
                    </label>
                    <textarea
                      value={capaForm.root_cause}
                      onChange={(e) => setCAPAForm({...capaForm, root_cause: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows={2}
                      placeholder="What caused this issue?"
                    />
                  </div>

                  {/* Corrective Action */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Corrective Action
                    </label>
                    <textarea
                      value={capaForm.corrective_action}
                      onChange={(e) => setCAPAForm({...capaForm, corrective_action: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows={2}
                      placeholder="Actions to correct this specific issue"
                    />
                  </div>

                  {/* Preventive Action */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preventive Action
                    </label>
                    <textarea
                      value={capaForm.preventive_action}
                      onChange={(e) => setCAPAForm({...capaForm, preventive_action: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      rows={2}
                      placeholder="Actions to prevent recurrence"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Assign To */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Assign To <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={capaForm.assigned_to}
                        onChange={handleAssignedToChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select assignee</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} - {u.role}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={capaForm.due_date}
                        onChange={(e) => setCAPAForm({...capaForm, due_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        max="2099-12-31"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <strong>ℹ️ Note:</strong> This CAPA will be created in the main CAPA repository and linked to this audit finding. It will appear in the CAPA module with source marked as &quot;Audit&quot;.
                  </p>
                </div>
              </div>
              
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
                <button
                  onClick={() => setShowCAPAModal(false)}
                  className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={createCAPA}
                  disabled={!capaForm.title || !capaForm.assigned_to}
                  className="flex-1 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Create CAPA
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default ConductAudit;
